import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { BimRemoveWorkOrderObjectMutation } from 'gql/graphql';
import { useSetIsWorkOrderChangeRequired } from 'pages/Project/BimProject/hooks/useSetIsWorkOrderChangeRequired';

import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { bimRemoveWorkOrderObjectGql } from '../../bimWorkOrderQueries';
import type { BimWorkOrderStructureOperation } from '../../types';

import { useResetWorkOrderFiltersWithStructureOps } from './useOnSuccessStructureOps';

export const useBimRemoveWorkOrderObject = (operation?: BimWorkOrderStructureOperation, onSuccess?: () => void) => {
    const dispatch = useDispatch();

    const requestId = useMemo(() => `bimWorkOrder${operation ?? ''}`, [operation]);

    const [bimRemoveWorkOrderObjectReset] = useResetStoreData({ requestId });
    const { makeResetWorkOrderObjectFilter } = useResetWorkOrderFiltersWithStructureOps();

    const onSuccessLocal = useCallback(
        ({ payload }: { payload: BimRemoveWorkOrderObjectMutation }) => {
            bimRemoveWorkOrderObjectReset(undefined);
            onSuccess?.();
            makeResetWorkOrderObjectFilter(payload?.bimRemoveWorkOrderObject?.data?.itemIds);
        },
        [bimRemoveWorkOrderObjectReset, makeResetWorkOrderObjectFilter, onSuccess],
    );

    const [
        bimRemoveWorkOrderObjectData,
        bimRemoveWorkOrderObjectStatus,
        bimRemoveWorkOrderObjectError,
        bimRemoveWorkOrderObjectRequest,
    ] = useRequest({
        requestId,
        query: bimRemoveWorkOrderObjectGql,
        withEffect: false,
        dependenciesUpdater: onSuccessLocal,
    });

    useSetIsWorkOrderChangeRequired({
        useData: bimRemoveWorkOrderObjectData?.bimRemoveWorkOrderObject?.data,
        status: bimRemoveWorkOrderObjectStatus,
    });

    const bimRemoveWorkOrderObjectMakeRequest = useCallback(
        (workOrderId: number, contractObjectId: number) => {
            dispatch(bimRemoveWorkOrderObjectRequest({ workOrderId, contractObjectId }));
        },
        [bimRemoveWorkOrderObjectRequest, dispatch],
    );

    useEffect(() => {
        bimRemoveWorkOrderObjectReset(undefined);
    }, [bimRemoveWorkOrderObjectReset]);

    return {
        bimRemoveWorkOrderObjectData,
        bimRemoveWorkOrderObjectStatus,
        bimRemoveWorkOrderObjectError,
        bimRemoveWorkOrderObjectMakeRequest,
    };
};
